import React, { useState } from 'react'
import { useRouteMatch } from 'react-router-dom'
import { gql, useQuery } from '@apollo/client'
import parseISO from 'date-fns/parseISO'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Typography from '@material-ui/core/Typography'
import Dialog from 'components/dialog'
import { useDialogTitleStyles } from 'components/dialog/use-dialog-title-styles'
import {
  JRLCoachCodeOfConduct,
  JRLManagerCodeOfConduct,
  JRLCoachWaiver,
} from 'components/waivers'
import * as maybe from 'lib/maybe'
import { PageHeader } from 'components/page-header'
import ShieldIcon from 'components/material-symbol/icons/Shield'
import DescriptionIcon from 'components/material-symbol/icons/Description'
import CloseIcon from 'components/material-symbol/icons/Close'
import BackgroundCheck, {
  statuses as backgroundCheckStatuses,
} from './background-check'
import Aps, { statuses as apsStatuses } from './aps'
import RequirementCard from './requirement-card'
import useAccountDetailsStyles from 'pages/account/account-details/styles'
import { useRequirementDialogStyles } from './styles'

export const GET_REQUIREMENT_STATUSES = gql`
  query JRLRequirementStatuses {
    currentUser {
      jrlSignedUp
      account {
        apsTraining {
          status
        }
        backgroundScreen {
          status
          passedAt
        }
        jrlUser {
          coachWaiver
          managerWaiver
        }
      }
    }
  }
`

const RequirementsDialog = ({ heading, open, children, onClose }) => {
  const titleClasses = useDialogTitleStyles()
  const classes = useRequirementDialogStyles()

  return (
    <Dialog
      showCloseButton
      isOpen={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      classes={{ paper: `${titleClasses.dialogPaper} ${classes.paper}` }}
    >
      {({ closeDialog }) => (
        <>
          <DialogTitle
            disableTypography
            classes={{ root: titleClasses.titleRoot }}
          >
            <Typography component="h2" className={titleClasses.title}>
              {heading}
            </Typography>
          </DialogTitle>
          <DialogContent classes={{ root: titleClasses.content }}>
            <div className={classes.legalDoc}>{children}</div>
          </DialogContent>
          <DialogActions className={titleClasses.actions}>
            <Button
              onClick={closeDialog}
              color="primary"
              variant="contained"
              className={classes.pillButton}
              startIcon={<CloseIcon fontSize="small" />}
            >
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

export const getStatuses = ({ data, loading, error }) => {
  if (loading) {
    return {
      backgroundScreen: {
        status: backgroundCheckStatuses.CHECKING,
        passedAt: null,
      },
      apsTraining: { status: apsStatuses.CHECKING },
    }
  }

  if (error) {
    return {
      backgroundScreen: {
        status: backgroundCheckStatuses.ERROR,
        passedAt: null,
      },
      apsTraining: { status: apsStatuses.ERROR },
    }
  }

  const { currentUser } = data

  return currentUser.account
}

const RequirementsPage = () => {
  const classes = useAccountDetailsStyles()
  const { path } = useRouteMatch()
  const crumbs = [
    {
      label: 'Account',
      to: path
        .split('/')
        .slice(0, 2)
        .join('/'),
    },
  ]

  const [activeDialog, setActiveDialog] = useState(null)

  const viewJRLCoachCodeOfConduct = () =>
    setActiveDialog('jrlCoachCodeOfConduct')
  const viewJRLManagerCodeOfConduct = () =>
    setActiveDialog('jrlManagerCodeOfConduct')
  const viewJRLWaiver = () => setActiveDialog('jrlWaiver')

  const closeActiveDialog = () => {
    setActiveDialog(null)
  }

  const { data, loading, error, refetch } = useQuery(GET_REQUIREMENT_STATUSES)

  const { backgroundScreen, apsTraining } = getStatuses({
    data,
    loading,
    error,
  })
  const { account, jrlSignedUp } = data?.currentUser || {}

  const waiverActions = []

  if (jrlSignedUp) {
    waiverActions.push({
      onClick: viewJRLWaiver,
      title: 'View PGA Jr. League Waiver',
      icon: <DescriptionIcon />,
    })
    if (account.jrlUser.coachWaiver) {
      waiverActions.push({
        onClick: viewJRLCoachCodeOfConduct,
        title: 'View PGA Jr. League Coach Code of Conduct',
        icon: <ShieldIcon />,
      })
    }
    if (account.jrlUser.managerWaiver) {
      waiverActions.push({
        onClick: viewJRLManagerCodeOfConduct,
        title: 'View PGA Jr. League Manager Code of Conduct',
        icon: <ShieldIcon />,
      })
    }
  }

  return (
    <>
      <PageHeader title="Requirements" crumbs={crumbs} />
      <Box className={classes.contentContainer}>
        <Box className={classes.stack}>
          <BackgroundCheck
            status={backgroundScreen?.status || 'NOT_STARTED'}
            passedAt={maybe.map(backgroundScreen?.passedAt, parseISO)}
            refetchStatus={refetch}
          />

          <Aps status={apsTraining?.status || 'NOT_STARTED'} />

          {waiverActions.length > 0 ? (
            <RequirementCard
              title="Code of Conduct and Waiver"
              actions={waiverActions}
              testid="waiver-card"
            >
              Upon signup you will have agreed to the Code of Conduct and
              Waiver. To reference a copy of these agreements, please click
              below.
            </RequirementCard>
          ) : null}
        </Box>
      </Box>

      <RequirementsDialog
        heading="PGA Jr. League Waiver"
        open={activeDialog === 'jrlWaiver'}
        onClose={closeActiveDialog}
      >
        <JRLCoachWaiver />
      </RequirementsDialog>

      <RequirementsDialog
        heading="PGA Jr. League Coach Code of Conduct"
        open={activeDialog === 'jrlCoachCodeOfConduct'}
        onClose={closeActiveDialog}
      >
        <JRLCoachCodeOfConduct />
      </RequirementsDialog>

      <RequirementsDialog
        heading="PGA Jr. League Manager Code of Conduct"
        open={activeDialog === 'jrlManagerCodeOfConduct'}
        onClose={closeActiveDialog}
      >
        <JRLManagerCodeOfConduct />
      </RequirementsDialog>
    </>
  )
}

export default RequirementsPage
